import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  ClipboardList,
  Edit2,
  Trash2,
  X,
  Clock,
  Calendar,
  Truck,
  User,
  AlertCircle,
  CheckCircle,
  Camera,
  Image,
  MapPin,
  Filter,
  Wrench,
  WifiOff,
  Cloud,
  History,
  ChevronDown,
  ChevronUp,
  FileText,
  Share2,
  RotateCcw,
  RefreshCw,
  XCircle,
  Archive,
  CheckCircle2,
  Circle,
  ListChecks,
  CalendarClock
} from 'lucide-react';
import ImageUpload, { uploadOfflinePhoto } from '../components/ImageUpload';
import MaintenanceFields, { maintReadyToClose, maintMissingItems, MaterialsSection } from '../components/MaintenanceFields';
import ChecklistConfigModal from '../components/ChecklistConfigModal';
import DiariaPanelModal from '../components/DiariaPanelModal';
import MaintenanceAlert from '../components/MaintenanceAlert';
import {
  getAllFromStore,
  createWorkOrderOffline,
  updateWorkOrderOffline,
  mirrorWorkOrdersCache,
  cacheMatchesCurrentTenant,
  saveToStore,
  getLastSyncTime,
  STORES,
  isOnline
} from '../utils/offlineDB';
import { authHeaders } from '../utils/api';
import { fetchJsonResilient } from '../utils/resilientFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Data LOCAL (YYYY-MM-DD) — evita o desvio de UTC do toISOString(), que a partir
// das 21h (BRT) considerava "hoje" como o dia seguinte, escondendo O.S. do dia.
const localDateStr = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

export default function WorkOrders({ userRole, user }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);
  const [locations, setLocations] = useState([]);
  const [operationTypes, setOperationTypes] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [almoxItens, setAlmoxItens] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);
  const [showChecklistConfig, setShowChecklistConfig] = useState(false);
  const [showDiariaPanel, setShowDiariaPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tipoOsFilter, setTipoOsFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('month');
  const [machineFilter, setMachineFilter] = useState('all');
  const [operationTypeFilter, setOperationTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [operatorFilters, setOperatorFilters] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [quickCloseMode, setQuickCloseMode] = useState(false);
  const [reopenModal, setReopenModal] = useState({ open: false, order: null, motivo: '', submitting: false });
  const [cancelModal, setCancelModal] = useState({ open: false, order: null, motivo: '', submitting: false });
  const [formSubmitting, setFormSubmitting] = useState(false); // proteção contra dupla submissão
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'cancelled'
  const [refreshing, setRefreshing] = useState(false);
  const [offlineDataAt, setOfflineDataAt] = useState(null); // timestamp do cache local exibido (null = dados frescos)
  const [lastFetchAt, setLastFetchAt] = useState(null); // última sincronização bem-sucedida com o servidor
  const [currentLocation, setCurrentLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(null); // Para expandir histórico de edições
  const [form, setForm] = useState({
    date: localDateStr(),
    setor_operacao_id: '',
    machine_id: '',
    operator_id: '',
    location_id: '',
    operation_type: '',
    start_hour_meter: 0,
    end_hour_meter: '',
    observations: '',
    maintenance_items: '',  // Itens de manutenção (filtros, peças, lubrificantes)
    start_hour_meter_photo: '',
    end_hour_meter_photo: '',
    start_location: null,
    end_location: null,
    // === Bloco A — O.S. de Manutenção ===
    tipo_execucao: '',
    problema_reclamado: '', problema_encontrado: '', diagnostico: '', causa_raiz: '', prioridade_manutencao: '',
    recomendacao_futura: '',
    data_hora_inicio_parada: '', data_hora_fim_parada: '', duracao_parada_horas: '',
    maintenance_photos: {},
    labor_entries: [], materials: [], materials_photo: '', post_checklist: [],
    gerar_diaria: false, diaria: null,
    assinatura_desenho_dataurl: '', assinatura_desenho_url: '', assinatura_executor_nome: '', confirmacao_senha: '', assinatura_fechamento: null,
  });

  // Função para formatar data/hora do histórico
  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Função para traduzir nomes de campos
  const translateFieldName = (field) => {
    const translations = {
      'end_hour_meter': 'Horímetro Final',
      'observations': 'Observações',
      'maintenance_items': 'Itens de Manutenção',
      'status': 'Status',
      'end_hour_meter_photo': 'Foto Horímetro Final',
      'operation_type': 'Tipo de Operação',
      'os_type': 'Tipo de O.S.'
    };
    return translations[field] || field;
  };

  // Renderiza com segurança o valor de uma mudança no histórico.
  // Evita o crash "Objects are not valid as a React child" para campos complexos
  // (labor_entries, materials, diaria, post_checklist, assinatura_fechamento, etc.).
  const formatChangeValue = (v) => {
    if (v === null || v === undefined || v === '') return '(vazio)';
    if (typeof v === 'object') {
      if (Array.isArray(v)) return v.length ? `${v.length} item(ns)` : '(vazio)';
      return '(atualizado)';
    }
    if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
    return String(v);
  };

  // Função para traduzir papel do usuário
  const translateRole = (role) => {
    const translations = {
      'admin': 'Administrador',
      'gestor': 'Gestor',
      'supervisor': 'Supervisor',
      'tecnico': 'Técnico',
      'operador': 'Operador'
    };
    return translations[role] || role;
  };

  // Função para capturar localização GPS
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        resolve(null); // GPS não disponível, continua sem bloquear
        return;
      }

      setGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          };
          setCurrentLocation(locationData);
          setGettingLocation(false);
          resolve(locationData);
        },
        (error) => {
          console.warn('Erro ao obter localização:', error.message);
          setGettingLocation(false);
          resolve(null); // Continua sem bloquear se falhar
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    });
  };

  // Capturar localização ao abrir o modal de nova O.S.
  const openNewOrderModal = async () => {
    setEditingOrder(null);
    resetForm();
    setShowModal(true);
    
    // Capturar GPS automaticamente
    const location = await getCurrentLocation();
    if (location) {
      setForm(prev => ({ ...prev, start_location: location }));
      toast.success(`📍 Localização capturada`);
    }
  };

  const isOperator = userRole === 'operador';

  // --- RBAC helpers: Operador só manipula O.S. de Operação/Produção ---
  const isMaintenanceOs = (osType) => typeof osType === 'string' && osType.startsWith('Manutenção');

  // Bloco A — mapeia os_type/tipo_os para tipo detalhado de manutenção
  const osTypeToDetalhado = (t) =>
    t === 'Manutenção Corretiva' ? 'manutencao_corretiva'
      : t === 'Manutenção Preventiva' ? 'manutencao_preventiva'
      : null;
  // Apenas Master/Gestor editam tipo_execucao e tempo de parada manualmente
  const canEditExecParada = !!(user?.is_master || userRole === 'gestor');

  // Quem pode aplicar peças/materiais em O.S. Operacional (baixa de estoque ao fechar):
  // Master/Admin/Gestor/Supervisor/Aux. de Almoxarifado. Operador NÃO aplica peças.
  const podeAplicarPecas = !!(
    user?.is_master || user?.is_master_auxiliar ||
    ['admin', 'gestor', 'supervisor'].includes(userRole) ||
    user?.acesso_almoxarifado || user?.acesso_inventario
  );

  const podeCriarOS = () => {
    // Operador pode criar, mas com tipo restrito (tratado no form). Demais seguem regra existente.
    return true;
  };

  const podeEditarOS = (order) => {
    if (!order) return false;
    if (userRole === 'operador') {
      // Operador só edita/fecha O.S. abertas e que não sejam de Manutenção
      return order.status === 'open' && !isMaintenanceOs(order.os_type);
    }
    // Demais roles: admin/gestor/supervisor podem sempre; outros só enquanto aberta
    return order.status === 'open' || ['admin', 'gestor', 'supervisor'].includes(userRole);
  };

  // Reabrir O.S.: admin/gestor/master sempre; demais roles precisam flag reabrir_os
  const podeReabrirOS = (order) => {
    if (!order || order.status !== 'closed') return false;
    if (user?.is_master) return true;
    if (['admin', 'gestor'].includes(userRole)) return true;
    const flagFromPermissions = Boolean(user?.permissions?.reabrir_os);
    const flagTopLevel = Boolean(user?.reabrir_os);
    return flagFromPermissions || flagTopLevel;
  };

  // Cancelar O.S.: mesma regra do editar (operador só Operacional/Produção abertas dele;
  // admin/master/gestor/supervisor sempre). Não permite cancelar deletada ou já cancelada.
  const podeCancelarOS = (order) => {
    if (!order) return false;
    if (order.status === 'cancelled' || order.status === 'deleted') return false;
    return podeEditarOS(order) || ['admin', 'gestor', 'supervisor'].includes(userRole) || user?.is_master;
  };

  // Função para calcular datas de período (sempre em data LOCAL, nunca UTC)
  const getPeriodDates = (period) => {
    const today = new Date();
    const todayStr = localDateStr(today);
    if (period === 'today') return { from: todayStr, to: todayStr };
    if (period === '7days') {
      const d = new Date(today); d.setDate(d.getDate() - 7);
      return { from: localDateStr(d), to: todayStr };
    }
    if (period === 'month') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: localDateStr(first), to: todayStr };
    }
    return null; // 'all' -> sem filtro
  };

  useEffect(() => {
    fetchData();
    if (isOperator) fetchOperatorFilters();
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Etapa 1 Jul/2026 — Setores de Operação do Local selecionado (para O.S. Operacional)
  const [setoresLocal, setSetoresLocal] = useState([]);
  useEffect(() => {
    const lid = form.location_id;
    if (!lid) { setSetoresLocal([]); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/setores-operacao?local_operacao_id=${lid}&status=ativo`, { headers: authHeaders() });
        const data = r.ok ? await r.json() : [];
        setSetoresLocal(data);
        setForm((f) => (f.setor_operacao_id && !data.some((s) => s.id === f.setor_operacao_id)
          ? { ...f, setor_operacao_id: '' } : f));
      } catch (_) { setSetoresLocal([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.location_id]);

  // Rastreabilidade (Jul/2026): permite chegar com ?search=%231234 (link vindo da Conta Corrente)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const s = sp.get('search');
    if (s) setSearch(s);
  }, []);

  const fetchOperatorFilters = async () => {
    try {
      const response = await fetch(`${API_URL}/api/work-orders/operator-filters`, { headers: authHeaders() });
      if (response.ok) setOperatorFilters(await response.json());
    } catch {}
  };

  // Botão "Atualizar agora" — força recarga e informa a ORIGEM real dos dados exibidos
  const handleManualRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const result = await fetchData();
      if (result === 'fresh') {
        toast.success('Dados atualizados');
      } else if (result === 'cache') {
        toast.warning('Sem conexão com o servidor — exibindo dados locais (podem estar desatualizados). Tente novamente em instantes.');
      } else {
        toast.error('Falha ao atualizar os dados. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const fetchData = async () => {
    let loadStatus = 'none'; // 'fresh' | 'cache' | 'none'
    try {
      const headers = authHeaders();
      const ordersUrl = viewMode === 'cancelled'
        ? `${API_URL}/api/work-orders?status_filter=cancelled`
        : `${API_URL}/api/work-orders`;
      const fresh = { headers, cache: 'no-store' };

      // CARREGAMENTO RESILIENTE DA LISTA (util compartilhado): trata 503 do SW,
      // desafios Cloudflare (403/429) e respostas HTML como transitórios, com
      // retry+backoff. Só usa o cache local após esgotar as tentativas, garantindo
      // que a lista de O.S. (abertas e fechadas) nunca fique vazia quando há dados.
      const ordersData = await fetchJsonResilient(ordersUrl, fresh);
      if (ordersData) {
        setWorkOrders(ordersData);
        setOfflineDataAt(null);
        setLastFetchAt(new Date());
        loadStatus = 'fresh';
        // Espelha o cache offline a CADA busca bem-sucedida da lista completa:
        // O.S. fechadas/canceladas no servidor deixam de aparecer "abertas" no cache.
        if (viewMode !== 'cancelled') {
          mirrorWorkOrdersCache(ordersData).catch(() => {});
        }
      } else {
        // Rede indisponível após retries → usa cache local (não deixa vazio),
        // SEMPRE sinalizando de forma explícita que os dados podem estar desatualizados.
        // Segurança multi-tenant: só exibe o cache se ele pertence ao tenant ATIVO.
        try {
          const sameTenant = await cacheMatchesCurrentTenant();
          const cachedOrders = sameTenant ? await getAllFromStore(STORES.WORK_ORDERS) : [];
          if (cachedOrders.length > 0) {
            setWorkOrders(cachedOrders);
            loadStatus = 'cache';
            const ts = await getLastSyncTime().catch(() => null);
            setOfflineDataAt(ts || 'desconhecida');
          }
        } catch (_) { /* sem cache: mantém estado atual */ }
      }

      // 2) Dados de apoio — best-effort: nunca derrubam a lista
      const [machinesRes, operatorsRes, locationsRes, typesRes] = await Promise.all([
        fetch(`${API_URL}/api/machines`, fresh).catch(() => null),
        fetch(`${API_URL}/api/operators`, fresh).catch(() => null),
        fetch(`${API_URL}/api/locations`, fresh).catch(() => null),
        fetch(`${API_URL}/api/operation-types`, fresh).catch(() => null),
      ]);
      if (machinesRes && machinesRes.ok) setMachines(await machinesRes.json());
      if (operatorsRes && operatorsRes.ok) setOperators(await operatorsRes.json());
      if (locationsRes && locationsRes.ok) setLocations(await locationsRes.json());
      if (typesRes && typesRes.ok) {
        const data = await typesRes.json();
        setOperationTypes(data.types || []);
      }

      // 3) Auxiliares de manutenção (não bloqueiam o carregamento principal)
      Promise.all([
        fetch(`${API_URL}/api/colaboradores/options`, fresh).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_URL}/api/almoxarifado/itens`, fresh).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_URL}/api/os-checklist-templates/effective`, fresh).then(r => r.ok ? r.json() : { items: [] }).catch(() => ({ items: [] })),
        fetch(`${API_URL}/api/fornecedores`, fresh).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_URL}/api/diaria-tipos-servico/effective`, fresh).then(r => r.ok ? r.json() : { items: [] }).catch(() => ({ items: [] })),
      ]).then(([cols, itens, eff, forns, tipos]) => {
        setColaboradores(Array.isArray(cols) ? cols : []);
        setAlmoxItens(Array.isArray(itens) ? itens : []);
        setChecklistItems((eff && eff.items) || []);
        setFornecedores(Array.isArray(forns) ? forns : []);
        setTiposServico((tipos && tipos.items) || []);
      });

    } catch (error) {
      console.warn('📴 Falha inesperada ao carregar O.S. — tentando cache local', error);
      try {
        const sameTenant = await cacheMatchesCurrentTenant();
        const [cachedOrders, cachedMachines, cachedOperators, cachedLocations] = sameTenant ? await Promise.all([
          getAllFromStore(STORES.WORK_ORDERS),
          getAllFromStore(STORES.MACHINES),
          getAllFromStore(STORES.OPERATORS),
          getAllFromStore(STORES.LOCATIONS)
        ]) : [[], [], [], []];
        if (cachedOrders.length > 0) {
          setWorkOrders(cachedOrders);
          loadStatus = 'cache';
          const ts = await getLastSyncTime().catch(() => null);
          setOfflineDataAt(ts || 'desconhecida');
        }
        if (cachedMachines.length > 0) setMachines(cachedMachines);
        if (cachedOperators.length > 0) setOperators(cachedOperators);
        if (cachedLocations.length > 0) setLocations(cachedLocations);
      } catch (cacheError) {
        toast.error('Erro de conexão');
      }
    } finally {
      setLoading(false);
    }
    return loadStatus;
  };

  const handleSubmit = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    if (formSubmitting) return; // bloqueia dupla submissão (duplo clique / dupla criação)
    setFormSubmitting(true);
    try {
    const { forceDraft = false } = opts;

    // Tipo detalhado de manutenção (edição usa os_type da O.S.; criação usa form.tipo_os)
    const osDetalhado = osTypeToDetalhado(editingOrder ? editingOrder.os_type : form.tipo_os);
    const closing = !forceDraft && !!form.end_hour_meter;

    const url = editingOrder 
      ? `${API_URL}/api/work-orders/${editingOrder.id}`
      : `${API_URL}/api/work-orders`;
    
    // Se estiver fechando O.S., capturar localização final
    let endLocation = form.end_location;
    if (editingOrder && closing && !endLocation) {
      endLocation = await getCurrentLocation();
    }

    // Campos de manutenção (apenas quando for O.S. de manutenção)
    const maintPayload = osDetalhado ? {
      tipo_execucao: form.tipo_execucao || undefined,
      problema_reclamado: form.problema_reclamado || undefined,
      problema_encontrado: form.problema_encontrado || undefined,
      diagnostico: form.diagnostico || undefined,
      causa_raiz: form.causa_raiz || undefined,
      prioridade_manutencao: form.prioridade_manutencao || undefined,
      recomendacao_futura: form.recomendacao_futura || undefined,
      data_hora_inicio_parada: form.data_hora_inicio_parada || undefined,
      data_hora_fim_parada: form.data_hora_fim_parada || undefined,
      maintenance_photos: form.maintenance_photos && Object.keys(form.maintenance_photos).length ? form.maintenance_photos : undefined,
      // Fase 2
      labor_entries: Array.isArray(form.labor_entries) ? form.labor_entries : undefined,
      materials: Array.isArray(form.materials) ? form.materials : undefined,
      materials_photo: form.materials_photo || undefined,
      post_checklist: Array.isArray(form.post_checklist) && form.post_checklist.length ? form.post_checklist : undefined,
      // Fase 4 — Diária
      gerar_diaria: form.gerar_diaria ? true : (form.gerar_diaria === false ? false : undefined),
      diaria: form.gerar_diaria && form.diaria ? form.diaria : undefined,
    } : {};

    // Peças/materiais em O.S. Operacional (sem foto) — apenas perfis autorizados
    const isOperationalOs = !osDetalhado
      && (editingOrder ? editingOrder.os_type : form.tipo_os) === 'Operacional';
    const pecasPayload = (isOperationalOs && podeAplicarPecas)
      ? { materials: Array.isArray(form.materials) ? form.materials : undefined }
      : {};

    const payload = editingOrder 
      ? {
          end_hour_meter: closing ? parseFloat(form.end_hour_meter) : null,
          end_hour_meter_photo: form.end_hour_meter_photo || null,
          end_location: closing ? endLocation : null,
          observations: form.observations,
          maintenance_items: form.maintenance_items || null,
          ...maintPayload,
          ...pecasPayload,
          // Apenas Admin/Master pode trocar operador
          ...((userRole === 'admin' || user?.is_master) && form.operator_id !== editingOrder.operator_id ? { operator_id: form.operator_id } : {}),
          // Apenas Admin/Master pode trocar máquina — re-rastreia o horímetro inicial da nova máquina
          ...((userRole === 'admin' || user?.is_master) && form.machine_id !== editingOrder.machine_id
            ? { machine_id: form.machine_id, start_hour_meter: form.start_hour_meter !== '' && form.start_hour_meter != null ? parseFloat(form.start_hour_meter) : null }
            : {}),
          // Local de Operação editável — respeita o mesmo RBAC do endpoint PUT (check_edit_permission no backend)
          ...(form.location_id && form.location_id !== editingOrder.location_id ? { location_id: form.location_id } : {}),
          // Etapa 1 Jul/2026 — Setor de Operação (quando alterado)
          ...(form.setor_operacao_id && form.setor_operacao_id !== editingOrder.setor_operacao_id ? { setor_operacao_id: form.setor_operacao_id } : {}),
          // Rastreabilidade: quem está editando
          edited_by_id: user?.id || user?.user_id,
          edited_by_name: user?.name,
          edited_by_role: userRole
        }
      : {
          date: form.date,
          machine_id: form.machine_id,
          operator_id: form.operator_id,
          location_id: form.location_id,
          setor_operacao_id: form.setor_operacao_id || null,
          operation_type: form.operation_type,
          tipo_os: form.tipo_os,
          start_hour_meter: parseFloat(form.start_hour_meter),
          end_hour_meter: closing ? parseFloat(form.end_hour_meter) : null,
          start_hour_meter_photo: form.start_hour_meter_photo || null,
          end_hour_meter_photo: form.end_hour_meter_photo || null,
          start_location: form.start_location,
          end_location: closing ? endLocation : null,
          observations: form.observations,
          maintenance_items: form.maintenance_items || null,
          ...maintPayload,
          ...pecasPayload,
          // Identificação do criador para tipo de O.S.
          created_by_id: user?.id || user?.user_id,
          created_by_name: user?.name,
          created_by_role: userRole
        };

    // Validação
    if (!editingOrder && !form.location_id) {
      toast.error('Selecione o local de operação');
      return;
    }

    // Etapa 1 Jul/2026 — Setor obrigatório em O.S. Operacional quando o local possui setores ativos
    if (!editingOrder && form.tipo_os === 'Operacional' && setoresLocal.length > 0 && !form.setor_operacao_id) {
      toast.error('Este local possui Setores de Operação — selecione o setor');
      return;
    }
    
    if (!editingOrder && !form.operation_type) {
      toast.error('Selecione o tipo de operação');
      return;
    }

    if (!editingOrder && !form.tipo_os) {
      toast.error('Selecione o Tipo de O.S.');
      return;
    }

    if (!editingOrder && parseFloat(form.start_hour_meter) < 0) {
      toast.error('Horímetro inicial não pode ser negativo');
      return;
    }
    
    // O.S. de manutenção JÁ FECHADA (legada): edições continuam permitidas sem re-exigir itens do fechamento
    const jaFechada = !!editingOrder && editingOrder.status === 'closed';

    if (closing && form.start_hour_meter !== '' && form.start_hour_meter != null &&
        parseFloat(form.end_hour_meter) < parseFloat(form.start_hour_meter)) {
      toast.error('Horímetro final deve ser maior que o inicial');
      return;
    }

    // Validação: Foto final obrigatória para FECHAR O.S. (não bloqueia edição de O.S. já fechada legada sem foto)
    if (closing && !jaFechada && !form.end_hour_meter_photo) {
      toast.error('Foto do horímetro final é obrigatória para fechar a O.S.');
      return;
    }

    // Bloco A — fechar O.S. de manutenção exige campos/fotos obrigatórios (evita 422 do backend).
    if (closing && osDetalhado && !jaFechada && !maintReadyToClose(form, osDetalhado)) {
      toast.error('Preencha todos os campos, fotos e a assinatura da manutenção antes de fechar a O.S.');
      return;
    }

    // Fase 5 — Assinatura combinada (desenho + senha): regra vem do BACKEND (requer_senha_fechamento).
    // Toda transição open->closed de O.S. de manutenção exige senha — inclusive O.S. REABERTAS.
    if (closing && osDetalhado && editingOrder && !jaFechada && (editingOrder.requer_senha_fechamento !== false)) {
      if (!form.confirmacao_senha || !form.confirmacao_senha.trim()) {
        toast.error('Informe sua senha para confirmar o fechamento da O.S.');
        return;
      }
      try {
        let desenhoUrl = form.assinatura_desenho_url;
        if (form.assinatura_desenho_dataurl && String(form.assinatura_desenho_dataurl).startsWith('data:')) {
          desenhoUrl = await uploadOfflinePhoto(form.assinatura_desenho_dataurl, 'manutencao');
        }
        if (!desenhoUrl) {
          toast.error('Assinatura desenhada é obrigatória para fechar a O.S.');
          return;
        }
        payload.assinatura_desenho_url = desenhoUrl;
        payload.assinatura_executor_nome = form.assinatura_executor_nome;
        payload.confirmacao_senha = form.confirmacao_senha;
      } catch (err) {
        toast.error('Falha ao enviar a assinatura. Verifique a conexão e tente novamente.');
        return;
      }
    }

    try {
      const online = isOnline();
      
      if (online) {
        // Online: enviar para o servidor
        const response = await fetch(url, {
          method: editingOrder ? 'PUT' : 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          // Atualiza o cache offline IMEDIATAMENTE com o estado do servidor:
          // uma O.S. fechada nunca mais fica "aberta" no cache local.
          try {
            const saved = await response.json();
            if (saved?.id) await saveToStore(STORES.WORK_ORDERS, { ...saved, synced: true });
          } catch (_) { /* cache é best-effort */ }
          toast.success(
            forceDraft ? 'Rascunho salvo!' : (editingOrder ? 'O.S. atualizada!' : 'O.S. criada com sucesso!')
          );
          setShowModal(false);
          resetForm();
          fetchData();
        } else {
          const data = await response.json().catch(() => ({}));
          let msg = `Erro ao salvar (HTTP ${response.status})`;
          if (typeof data.detail === 'string') {
            msg = data.detail;
          } else if (Array.isArray(data.detail)) {
            // 422 de validação (Pydantic): mostra campo e motivo
            msg = 'Dados inválidos: ' + data.detail
              .slice(0, 3)
              .map(d => `${(d.loc || []).filter(l => l !== 'body').join('.')}: ${d.msg}`)
              .join('; ');
          } else if (data.detail && typeof data.detail === 'object') {
            const pend = data.detail.pendencias;
            msg = (data.detail.message || 'Itens obrigatórios pendentes')
              + (Array.isArray(pend) && pend.length ? ` (${pend.length} pendência(s))` : '');
            // Backend exige senha: habilita o campo no modal NA HORA (sem recarregar a página)
            if (Array.isArray(pend) && pend.includes('assinatura:senha')) {
              setForm(f => ({ ...f, assinatura_fechamento: null, confirmacao_senha: '' }));
              if (editingOrder) setEditingOrder(o => (o ? { ...o, requer_senha_fechamento: true } : o));
              msg = 'Confirmação por senha é obrigatória para fechar esta O.S. — o campo de senha foi habilitado na seção Assinatura de Fechamento.';
            }
          }
          toast.error(msg);
        }
      } else {
        // Offline: salvar localmente
        if (editingOrder) {
          await updateWorkOrderOffline(editingOrder.id, payload);
          toast.success('📴 O.S. atualizada offline! Será sincronizada quando voltar online.');
        } else {
          // Adicionar info das máquinas/operadores para exibição offline
          const machine = machines.find(m => m.id === form.machine_id);
          const operator = operators.find(o => o.id === form.operator_id);
          const location = locations.find(l => l.id === form.location_id);
          
          const enrichedPayload = {
            ...payload,
            machine_info: machine ? {
              id: machine.id,
              type: machine.type,
              brand: machine.brand,
              model: machine.model,
              serial_number: machine.serial_number
            } : null,
            operator_info: operator ? {
              id: operator.id,
              name: operator.name,
              document: operator.document
            } : null,
            location_info: location ? {
              id: location.id,
              name: location.name,
              full_name: location.full_name
            } : null
          };
          
          await createWorkOrderOffline(enrichedPayload);
          toast.success('📴 O.S. salva offline! Será sincronizada quando voltar online.');
        }
        
        setShowModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar O.S.');
    }
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta O.S.?')) return;

    try {
      const response = await fetch(`${API_URL}/api/work-orders/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (response.ok) {
        toast.success('O.S. excluída!');
        fetchData();
      } else {
        toast.error('Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  // Reabrir uma O.S. fechada (com motivo obrigatório)
  const openReopenModal = (order) => {
    setReopenModal({ open: true, order, motivo: '', submitting: false });
  };

  const handleReopenSubmit = async () => {
    const { order, motivo } = reopenModal;
    if (!order) return;
    const motivoTrim = (motivo || '').trim();
    if (motivoTrim.length < 10) {
      toast.error('O motivo da reabertura precisa ter no mínimo 10 caracteres');
      return;
    }
    setReopenModal((s) => ({ ...s, submitting: true }));
    try {
      const res = await fetch(`${API_URL}/api/work-orders/${order.id}/reopen`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ motivo: motivoTrim }),
      });
      if (res.ok) {
        toast.success('O.S. reaberta com sucesso');
        setReopenModal({ open: false, order: null, motivo: '', submitting: false });
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao reabrir O.S.');
        setReopenModal((s) => ({ ...s, submitting: false }));
      }
    } catch {
      toast.error('Erro de conexão');
      setReopenModal((s) => ({ ...s, submitting: false }));
    }
  };

  const openCancelModal = (order) => {
    setCancelModal({ open: true, order, motivo: '', submitting: false });
  };

  const handleCancelSubmit = async () => {
    const { order, motivo } = cancelModal;
    if (!order) return;
    const motivoTrim = (motivo || '').trim();
    if (motivoTrim.length < 10) {
      toast.error('O motivo do cancelamento precisa ter no mínimo 10 caracteres');
      return;
    }
    setCancelModal((s) => ({ ...s, submitting: true }));
    try {
      const res = await fetch(`${API_URL}/api/work-orders/${order.id}/cancel`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ motivo: motivoTrim }),
      });
      if (res.ok) {
        toast.success('O.S. cancelada');
        setCancelModal({ open: false, order: null, motivo: '', submitting: false });
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao cancelar O.S.');
        setCancelModal((s) => ({ ...s, submitting: false }));
      }
    } catch {
      toast.error('Erro de conexão');
      setCancelModal((s) => ({ ...s, submitting: false }));
    }
  };

  // Exporta a lista de O.S. canceladas atualmente exibidas para CSV
  const exportCancelledCSV = () => {
    if (!filteredOrders || filteredOrders.length === 0) {
      toast.info('Nenhuma O.S. cancelada para exportar');
      return;
    }
    const headers = [
      'Numero','Status','Tipo','Maquina','Operador','Local','Inicio','Fim','Horas Trabalhadas','Cancelada Em','Cancelada Por','Motivo do Cancelamento'
    ];
    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val).replace(/"/g, '""').replace(/\r?\n/g, ' ');
      return /[",;\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = filteredOrders.map((o) => [
      o.order_number || (o.id ? o.id.slice(-6) : ''),
      o.status || '',
      o.os_type || o.operation_type || '',
      o.machine_name || o.machine_id || '',
      o.operator_name || o.operator_id || '',
      o.location_full_name || o.location_name || '',
      o.start_datetime || '',
      o.end_datetime || '',
      o.total_hours ?? '',
      o.cancelled_at || '',
      o.cancelled_by_name || '',
      o.cancelled_motivo || '',
    ].map(escapeCSV).join(';'));
    const csv = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `OS_canceladas_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSV exportado');
  };

  // Função para baixar PDF de uma O.S. individual
  const handleDownloadPDF = async (orderId, orderNumber, osType) => {
    try {
      toast.info('Gerando PDF...');
      const userName = localStorage.getItem('userName') || 'Responsável';
      const response = await fetch(`${API_URL}/api/work-orders/${orderId}/pdf?issued_by_name=${encodeURIComponent(userName)}`, {
        headers: authHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `OS_${orderNumber || orderId.slice(0, 8)}_${osType || 'Operacao'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('PDF baixado com sucesso!');
      } else {
        toast.error('Erro ao gerar PDF');
      }
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast.error('Erro ao baixar PDF');
    }
  };

  // Função para compartilhar O.S. via WhatsApp
  const handleShareWhatsApp = (order) => {
    const machineName = order.machine_info 
      ? `${order.machine_info.brand} ${order.machine_info.model}`
      : 'N/A';
    const operatorName = order.operator_info?.name || 'N/A';
    const osType = order.os_type || 'Operação';
    const statusText = order.status === 'closed' ? 'Fechada' : 'Aberta';
    const hoursWorked = order.hours_worked ? `${order.hours_worked.toFixed(1)}h` : 'Pendente';
    
    // Formatar data
    let dateStr = order.date;
    if (dateStr) {
      try {
        const [y, m, d] = dateStr.split('-');
        dateStr = `${d}/${m}/${y}`;
      } catch {
        // mantém original
      }
    }
    
    // Montar mensagem
    const message = `📋 *ORDEM DE SERVIÇO DE ${osType.toUpperCase()}*

*O.S. #${order.order_number || '-'}*
Status: ${statusText}

📅 Data: ${dateStr}
🚜 Máquina: ${machineName}
👷 Operador: ${operatorName}
⏱️ Horas: ${hoursWorked}
${order.observations ? `📝 Obs: ${order.observations}` : ''}
${order.maintenance_items ? `🔧 Itens de Manutenção: ${order.maintenance_items}` : ''}

---
Gestão de Máquinas
gestaodemaquinas.com.br`;

    // Codificar mensagem para URL
    const encodedMessage = encodeURIComponent(message);
    
    // Abrir WhatsApp
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    toast.success('WhatsApp aberto! Selecione o contato.');
  };

  const openEditModal = (order) => {
    setEditingOrder(order);
    setForm({
      date: order.date,
      machine_id: order.machine_id,
      operator_id: order.operator_id,
      location_id: order.location_id || '',
      setor_operacao_id: order.setor_operacao_id || '',
      operation_type: order.operation_type || '',
      start_hour_meter: order.start_hour_meter,
      end_hour_meter: order.end_hour_meter || '',
      observations: order.observations || '',
      maintenance_items: order.maintenance_items || '',
      start_hour_meter_photo: order.start_hour_meter_photo || '',
      end_hour_meter_photo: order.end_hour_meter_photo || '',
      start_location: order.start_location || null,
      end_location: order.end_location || null,
      // === Bloco A — O.S. de Manutenção ===
      tipo_execucao: order.tipo_execucao || '',
      problema_reclamado: order.problema_reclamado || '',
      problema_encontrado: order.problema_encontrado || '',
      diagnostico: order.diagnostico || '',
      causa_raiz: order.causa_raiz || '',
      prioridade_manutencao: order.prioridade_manutencao || '',
      recomendacao_futura: order.recomendacao_futura || '',
      data_hora_inicio_parada: order.data_hora_inicio_parada || '',
      data_hora_fim_parada: order.data_hora_fim_parada || '',
      duracao_parada_horas: order.duracao_parada_horas ?? '',
      maintenance_photos: order.maintenance_photos || {},
      labor_entries: order.labor_entries || [],
      materials: order.materials || [],
      materials_photo: order.materials_photo || '',
      post_checklist: order.post_checklist || [],
      gerar_diaria: !!order.gerar_diaria,
      diaria: order.diaria || null,
      // Fase 5 — Assinatura combinada (mantém a já existente; senha sempre em branco)
      assinatura_fechamento: order.assinatura_fechamento || null,
      assinatura_desenho_url: order.assinatura_fechamento?.assinatura_desenho_url || '',
      assinatura_desenho_dataurl: '',
      assinatura_executor_nome: order.assinatura_fechamento?.executor_nome || '',
      confirmacao_senha: '',
    });
    setShowModal(true);
  };

  // Fluxo rapido para fechar O.S. direto da listagem (captura GPS + foca no horimetro final)
  const openQuickCloseModal = async (order) => {
    openEditModal(order);
    setQuickCloseMode(true);
    // Dispara captura de GPS em paralelo (nao-bloqueante)
    try {
      const loc = await getCurrentLocation();
      if (loc) {
        setForm(prev => ({ ...prev, end_location: loc }));
      }
    } catch {}
  };

  // Quando modal abrir em modo rapido, focar no campo horimetro final
  useEffect(() => {
    if (showModal && quickCloseMode) {
      const t = setTimeout(() => {
        const el = document.querySelector('[data-testid="input-end-hour"]');
        if (el) {
          el.focus();
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      return () => clearTimeout(t);
    }
    if (!showModal) setQuickCloseMode(false);
  }, [showModal, quickCloseMode]);

  const openNewModal = () => {
    openNewOrderModal(); // Usa a nova função que captura GPS
  };

  const resetForm = () => {
    setEditingOrder(null);
    setCurrentLocation(null);
    setForm({
      date: localDateStr(),
      setor_operacao_id: '',
      machine_id: '',
      operator_id: '',
      location_id: '',
      operation_type: '',
      // Operador só cria O.S. Operacional -> default já selecionado
      tipo_os: userRole === 'operador' ? 'Operacional' : '',
      start_hour_meter: 0,
      end_hour_meter: '',
      observations: '',
      maintenance_items: '',
      start_hour_meter_photo: '',
      end_hour_meter_photo: '',
      start_location: null,
      end_location: null,
      // === Bloco A — O.S. de Manutenção ===
      tipo_execucao: '',
      problema_reclamado: '', problema_encontrado: '', diagnostico: '', causa_raiz: '', prioridade_manutencao: '',
      recomendacao_futura: '',
      data_hora_inicio_parada: '', data_hora_fim_parada: '', duracao_parada_horas: '',
      maintenance_photos: {},
      labor_entries: [], materials: [], materials_photo: '', post_checklist: [],
      gerar_diaria: false, diaria: null,
      assinatura_desenho_dataurl: '', assinatura_desenho_url: '', assinatura_executor_nome: '', confirmacao_senha: '', assinatura_fechamento: null,
    });
  };

  // Quando seleciona uma máquina, preencher horímetro inicial com o atual
  const handleMachineSelect = (machineId) => {
    const machine = machines.find(m => m.id === machineId);
    setForm({
      ...form, 
      machine_id: machineId,
      start_hour_meter: machine?.current_hour_meter || 0
    });
  };

  const filteredOrders = workOrders.filter(o => {
    const machineInfo = o.machine_info;
    const operatorInfo = o.operator_info;
    const searchLower = search.toLowerCase();
    
    // Filtro de status
    if (statusFilter !== 'all' && o.status !== statusFilter) {
      return false;
    }
    
    // Filtro de Tipo de O.S.
    if (tipoOsFilter !== 'all' && o.os_type !== tipoOsFilter) {
      return false;
    }

    // Filtro de período (Operador)
    if (isOperator && periodFilter !== 'all') {
      const dates = getPeriodDates(periodFilter);
      if (dates && o.date) {
        if (o.date < dates.from || o.date > dates.to) return false;
      }
    }

    // Filtro de máquina (Operador)
    if (machineFilter !== 'all' && o.machine_id !== machineFilter) return false;

    // Filtro de tipo de operação (Operador)
    if (operationTypeFilter !== 'all' && o.operation_type !== operationTypeFilter) return false;

    // Filtro de local (Operador)
    if (locationFilter !== 'all' && o.location_id !== locationFilter) return false;
    
    // Filtro de busca — todos os termos devem aparecer em algum campo
    // (data, nº da O.S., máquina [marca/modelo/série/tipo], operador, local, tipo de operação)
    const haystack = [
      o.date,
      o.order_number != null ? `os ${o.order_number} #${o.order_number}` : '',
      machineInfo?.brand, machineInfo?.model, machineInfo?.serial_number, machineInfo?.type,
      operatorInfo?.name, operatorInfo?.document,
      o.location_info?.name, o.location_info?.full_name,
      o.operation_type, o.os_type,
    ].filter(Boolean).join(' ').toLowerCase();
    const tokens = searchLower.split(/\s+/).filter(Boolean);
    return tokens.every(t => haystack.includes(t));
  }).sort((a, b) => {
    // Ordenação cronológica DESCENDENTE (mais recente no topo) — desktop e mobile/offline
    const da = (a.date || '');
    const db = (b.date || '');
    if (da !== db) return db.localeCompare(da);
    // Desempate por número da O.S. (maior = mais recente)
    return (b.order_number || 0) - (a.order_number || 0);
  });

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const activeMachines = machines.filter(m => m.status === 'active');
  const activeOperators = operators.filter(o => o.status === 'active');
  const activeLocations = locations.filter(l => l.status === 'active');

  return (
    <div className="space-y-6" data-testid="work-orders-page">
      {/* Alerta de Manutenção (não aplicável ao Operador) */}
      {!isOperator && <MaintenanceAlert showCompact={true} />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">
            {viewMode === 'cancelled' ? 'O.S. Canceladas' : (isOperator ? 'Minhas Ordens de Servico' : 'Ordens de Servico')}
          </h1>
          <p className="text-gray-400 mt-1">
            {viewMode === 'cancelled' ? 'Histórico de Ordens de Serviço canceladas' : (isOperator ? 'Registre e acompanhe sua producao diaria' : 'Controle de horas trabalhadas')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Toggle Ativas / Canceladas */}
          <div className="inline-flex rounded-lg bg-[#1A1A1A] border border-gray-800 p-1">
            <button
              onClick={() => setViewMode('active')}
              data-testid="view-mode-active"
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === 'active' ? 'bg-yellow-500 text-black font-semibold' : 'text-gray-300 hover:text-white'}`}
            >
              Ativas
            </button>
            <button
              onClick={() => setViewMode('cancelled')}
              data-testid="view-mode-cancelled"
              className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'cancelled' ? 'bg-orange-500 text-black font-semibold' : 'text-gray-300 hover:text-white'}`}
            >
              <Archive size={14} /> Canceladas
            </button>
          </div>
          {viewMode === 'cancelled' && filteredOrders.length > 0 && (
            <button
              onClick={() => exportCancelledCSV()}
              data-testid="export-cancelled-csv"
              className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg text-sm transition-colors"
              title="Exportar O.S. Canceladas em CSV"
            >
              <FileText size={16} /> Exportar CSV
            </button>
          )}
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            data-testid="refresh-work-orders"
            title="Atualizar agora (busca dados frescos do servidor)"
            className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-200 rounded-lg text-sm transition-colors"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{refreshing ? 'Atualizando…' : 'Atualizar'}</span>
          </button>
          {viewMode === 'active' && (
            <button
              onClick={openNewModal}
              data-testid="add-work-order-button"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
            >
              <Plus size={20} />
              <span>Nova O.S.</span>
            </button>
          )}
          {(user?.is_master || userRole === 'admin' || userRole === 'gestor') && (
            <button
              onClick={() => setShowChecklistConfig(true)}
              data-testid="open-checklist-config"
              title="Configurar Checklist Pós-Manutenção"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg transition-colors"
            >
              <ListChecks size={18} />
              <span className="hidden sm:inline">Checklist</span>
            </button>
          )}
          {(user?.is_master || userRole === 'admin' || userRole === 'gestor') && (
            <button
              onClick={() => setShowDiariaPanel(true)}
              data-testid="open-diaria-panel"
              title="Painel de Aprovação de Diárias"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg transition-colors"
            >
              <CalendarClock size={18} />
              <span className="hidden sm:inline">Diárias</span>
            </button>
          )}
        </div>
      </div>

      {/* Indicador permanente de última sincronização */}
      {(lastFetchAt || offlineDataAt) && (
        <div className="flex items-center gap-2 text-xs -mt-3" data-testid="last-sync-indicator">
          {offlineDataAt ? (
            <>
              <span className="w-2 h-2 rounded-full bg-amber-400 inline-block animate-pulse" />
              <span className="text-amber-400">
                Dados locais (sem conexão)
                {offlineDataAt !== 'desconhecida'
                  ? ` — última sincronização: ${new Date(offlineDataAt).toLocaleString('pt-BR')}`
                  : ''}
              </span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
              <span className="text-gray-400">
                Sincronizado com o servidor às {lastFetchAt.toLocaleTimeString('pt-BR')}
              </span>
            </>
          )}
        </div>
      )}

      {/* Aviso: dados exibidos vêm do cache local (sem conexão com o servidor) */}
      {offlineDataAt && (
        <div
          data-testid="offline-data-banner"
          className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/40 text-amber-300 rounded-lg px-4 py-3 text-sm"
        >
          <WifiOff size={18} className="shrink-0" />
          <span>
            Exibindo dados locais (sem conexão com o servidor)
            {offlineDataAt !== 'desconhecida'
              ? ` — última sincronização: ${new Date(offlineDataAt).toLocaleString('pt-BR')}`
              : ''}
            . Clique em "Atualizar" para buscar os dados mais recentes.
          </span>
        </div>
      )}

      {/* Operator summary bar */}
      {isOperator && filteredOrders.length > 0 && (
        <div className="grid grid-cols-3 gap-3" data-testid="operator-summary">
          <div className="bg-[#111] border border-gray-800 rounded-lg p-3 text-center">
            <p className="text-yellow-500 text-lg font-bold">
              {filteredOrders.filter(o => o.status === 'closed' && o.start_hour_meter != null && o.end_hour_meter != null)
                .reduce((sum, o) => sum + (o.end_hour_meter - o.start_hour_meter), 0).toFixed(1)}h
            </p>
            <p className="text-gray-400 text-xs">Horas no periodo</p>
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-lg p-3 text-center">
            <p className="text-white text-lg font-bold">{filteredOrders.length}</p>
            <p className="text-gray-400 text-xs">O.S. no periodo</p>
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-lg p-3 text-center">
            <p className="text-orange-400 text-lg font-bold">{filteredOrders.filter(o => o.status === 'open').length}</p>
            <p className="text-gray-400 text-xs">Em aberto</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder={isOperator ? "Buscar por data, máquina ou local..." : "Buscar por data, máquina, operador ou local..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
              data-testid="search-input"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
              data-testid="status-filter"
            >
              <option value="all">Todas</option>
              <option value="open">Abertas</option>
              <option value="closed">Fechadas</option>
            </select>
          </div>

          {/* Tipo de O.S. Filter (Operador só tem O.S. operacionais — filtro oculto) */}
          {!isOperator && (
          <div className="flex items-center gap-2">
            <select
              value={tipoOsFilter}
              onChange={(e) => setTipoOsFilter(e.target.value)}
              className="px-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
              data-testid="tipo-os-filter"
            >
              <option value="all">Todos os Tipos</option>
              <option value="Operacional">Operacional</option>
              <option value="Manutenção Preventiva">Manut. Preventiva</option>
              <option value="Manutenção Corretiva">Manut. Corretiva</option>
            </select>
          </div>
          )}
        </div>

        {/* Operator-specific filters */}
        {isOperator && (
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Period */}
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              className="px-4 py-3 bg-[#111] border border-yellow-500/30 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
              data-testid="period-filter"
            >
              <option value="today">Hoje</option>
              <option value="7days">Ultimos 7 dias</option>
              <option value="month">Mes atual</option>
              <option value="all">Todas</option>
            </select>

            {/* Machine filter (only operator's machines) */}
            {operatorFilters?.machines?.length > 0 && (
              <select
                value={machineFilter}
                onChange={(e) => setMachineFilter(e.target.value)}
                className="px-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                data-testid="machine-filter"
              >
                <option value="all">Todas as Maquinas</option>
                {operatorFilters.machines.map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.serial})</option>
                ))}
              </select>
            )}

            {/* Operation type filter (only operator's types) */}
            {operatorFilters?.operation_types?.length > 0 && (
              <select
                value={operationTypeFilter}
                onChange={(e) => setOperationTypeFilter(e.target.value)}
                className="px-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                data-testid="operation-type-filter"
              >
                <option value="all">Todas as Operacoes</option>
                {operatorFilters.operation_types.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            )}

            {/* Location filter (only operator's locations) */}
            {operatorFilters?.locations?.length > 0 && (
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="px-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                data-testid="location-filter"
              >
                <option value="all">Todos os Locais</option>
                {operatorFilters.locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredOrders.length > 0 ? (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div 
              key={order.id} 
              className="bg-[#111] border border-gray-800 rounded-xl p-4 lg:p-6 hover:border-gray-700 transition-all"
              data-testid={`work-order-card-${order.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Info Principal */}
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${order.status === 'open' ? 'bg-orange-500/10' : 'bg-green-500/10'}`}>
                    {order.status === 'open' ? (
                      <Clock size={24} className="text-orange-500" />
                    ) : (
                      <CheckCircle size={24} className="text-green-500" />
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {order.order_number && (
                        <span className="text-white font-bold text-sm bg-gray-800 px-2 py-1 rounded">
                          O.S. #{order.order_number}
                        </span>
                      )}
                      {/* Tipo de O.S. - Badge com cores por tipo (editável para Master) */}
                      {(() => {
                        const TIPO_OS_STYLES = {
                          'Operacional': 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
                          'Manutenção Preventiva': 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
                          'Manutenção Corretiva': 'bg-red-500/20 text-red-400 border border-red-500/30',
                        };
                        const osType = order.os_type || '';
                        const style = TIPO_OS_STYLES[osType] || 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
                        const label = TIPO_OS_STYLES[osType] ? osType : 'Não Informado';
                        const isMaster = user?.is_master;
                        if (isMaster) {
                          return (
                            <select
                              value={osType}
                              onClick={e => e.stopPropagation()}
                              onChange={async (e) => {
                                e.stopPropagation();
                                const newType = e.target.value;
                                const payload = { os_type: newType };
                                if (user?.id) payload.edited_by_id = user.id;
                                if (user?.name) payload.edited_by_name = user.name;
                                if (user?.role) payload.edited_by_role = user.role;
                                try {
                                  const r = await fetch(`${API_URL}/api/work-orders/${order.id}`, {
                                    method: 'PUT',
                                    headers: authHeaders({ 'Content-Type': 'application/json' }),
                                    body: JSON.stringify(payload)
                                  });
                                  if (r.ok) {
                                    toast.success(`Tipo alterado para ${newType}`);
                                    fetchData();
                                  } else {
                                    let msg = `HTTP ${r.status}`;
                                    try { const d = await r.json(); msg = typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail); } catch {}
                                    toast.error(msg);
                                  }
                                } catch (err) {
                                  console.error('OS type update error:', err);
                                  toast.error('Falha: ' + (err.message || 'verifique conexao'));
                                }
                              }}
                              className={`text-xs px-2 py-1 rounded font-medium cursor-pointer outline-none ${style}`}
                              data-testid={`os-type-select-${order.id}`}
                            >
                              <option value="Operacional">Operacional</option>
                              <option value="Manutenção Preventiva">Manut. Preventiva</option>
                              <option value="Manutenção Corretiva">Manut. Corretiva</option>
                            </select>
                          );
                        }
                        return (
                          <span className={`text-xs px-2 py-1 rounded font-medium ${style}`} data-testid={`os-type-badge-${order.id}`}>
                            {label}
                          </span>
                        );
                      })()}
                      <span className={`text-xs px-2 py-1 rounded ${
                        order.status === 'open' 
                          ? 'bg-orange-500/20 text-orange-400' 
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {order.status === 'open' ? 'Aberta' : 'Fechada'}
                      </span>
                      {/* Indicador de sync pendente */}
                      {order.synced === false && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400 flex items-center gap-1">
                          <Cloud size={12} />
                          Pendente sync
                        </span>
                      )}
                      {order.createdOffline && (
                        <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400 flex items-center gap-1">
                          <WifiOff size={12} />
                          Criada offline
                        </span>
                      )}
                      <span className="text-gray-500 text-sm flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(order.date)}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <div className="flex items-center gap-2 text-white">
                        <Truck size={16} className="text-yellow-500" />
                        <span className="font-medium">
                          {order.machine_info 
                            ? `${order.machine_info.brand} ${order.machine_info.model}` 
                            : 'Máquina não encontrada'}
                        </span>
                        {order.machine_info?.serial_number && (
                          <span className="text-gray-500 text-sm font-mono">
                            ({order.machine_info.serial_number})
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-gray-400">
                        <User size={16} />
                        <span>{order.operator_info?.name || 'Operador não encontrado'}</span>
                      </div>
                    </div>
                    
                    {/* Local e Tipo de Operação */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      {order.location_info && (
                        <div className="flex items-center gap-1 text-gray-400">
                          <MapPin size={14} className="text-green-500" />
                          <span>{order.location_info.full_name}</span>
                        </div>
                      )}
                      {order.operation_type && (
                        <div className="flex items-center gap-1 text-gray-400">
                          <Wrench size={14} className="text-blue-400" />
                          {(() => {
                            const canChangeOpType = user?.is_master && (!user?.is_master_auxiliar || user?.permissions?.alterar_tipo_operacao?.editar);
                            if (canChangeOpType && operationTypes.length > 0) {
                              return (
                                <select
                                  value={order.operation_type}
                                  onClick={e => e.stopPropagation()}
                                  onChange={async (e) => {
                                    e.stopPropagation();
                                    const newType = e.target.value;
                                    if (newType === order.operation_type) return;
                                    try {
                                      const r = await fetch(`${API_URL}/api/work-orders/${order.id}/operation-type`, {
                                        method: 'PATCH',
                                        headers: authHeaders({ 'Content-Type': 'application/json' }),
                                        body: JSON.stringify({
                                          operation_type: newType,
                                          edited_by_id: user?.id || user?.user_id,
                                          edited_by_name: user?.name,
                                          edited_by_role: userRole
                                        })
                                      });
                                      if (r.ok) {
                                        toast.success(`Tipo de operação alterado para "${newType}"`);
                                        fetchData();
                                      } else {
                                        let msg = `HTTP ${r.status}`;
                                        try { const d = await r.json(); msg = typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail); } catch {}
                                        toast.error(msg);
                                      }
                                    } catch (err) {
                                      toast.error('Erro de conexão');
                                    }
                                  }}
                                  className="bg-transparent text-blue-400 text-sm cursor-pointer outline-none border-b border-blue-400/30 hover:border-blue-400 transition-colors pr-1"
                                  data-testid={`operation-type-select-${order.id}`}
                                >
                                  {operationTypes.map(type => (
                                    <option key={type} value={type} className="bg-[#111] text-white">{type}</option>
                                  ))}
                                  {!operationTypes.includes(order.operation_type) && (
                                    <option value={order.operation_type} className="bg-[#111] text-white">{order.operation_type}</option>
                                  )}
                                </select>
                              );
                            }
                            return <span>{order.operation_type}</span>;
                          })()}
                        </div>
                      )}
                    </div>
                    
                    {/* GPS Coordinates */}
                    {(order.start_location || order.end_location) && (
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mt-1">
                        {order.start_location && (
                          <a 
                            href={`https://www.google.com/maps?q=${order.start_location.latitude},${order.start_location.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                            title={`Precisão: ${order.start_location.accuracy?.toFixed(0)}m`}
                          >
                            <MapPin size={12} />
                            <span>📍 Abertura</span>
                          </a>
                        )}
                        {order.end_location && (
                          <a 
                            href={`https://www.google.com/maps?q=${order.end_location.latitude},${order.end_location.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-green-400 hover:text-green-300"
                            title={`Precisão: ${order.end_location.accuracy?.toFixed(0)}m`}
                          >
                            <MapPin size={12} />
                            <span>📍 Fechamento</span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Horas */}
                <div className="flex items-center gap-6 lg:gap-8">
                  <div className="text-center">
                    <p className="text-gray-500 text-xs mb-1">Início</p>
                    <p className="text-white font-mono text-lg">{typeof order.start_hour_meter === 'number' ? `${order.start_hour_meter.toFixed(1)}h` : '—'}</p>
                  </div>
                  
                  {order.end_hour_meter && (
                    <>
                      <div className="text-center">
                        <p className="text-gray-500 text-xs mb-1">Fim</p>
                        <p className="text-white font-mono text-lg">{order.end_hour_meter?.toFixed(1)}h</p>
                      </div>
                      
                      <div className="text-center bg-yellow-500/10 px-4 py-2 rounded-lg">
                        <p className="text-yellow-500 text-xs mb-1">Trabalhadas</p>
                        <p className="text-yellow-500 font-bold text-xl">{typeof order.hours_worked === 'number' ? `${order.hours_worked.toFixed(1)}h` : '—'}</p>
                      </div>
                    </>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    {/* Botão Editar - Admin/Gestor/Supervisor podem editar O.S. fechadas; Operador só abertas de Operação/Produção */}
                    {podeEditarOS(order) && (
                      <button
                        onClick={() => openEditModal(order)}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                        data-testid={`edit-order-${order.id}`}
                        title={order.status === 'closed' ? 'Editar (Admin/Gestor/Supervisor)' : 'Editar / Fechar O.S.'}
                      >
                        <Edit2 size={18} />
                      </button>
                    )}
                    {/* Botão dedicado: Trocar Máquina / Atualizar O.S. (Admin/Master, O.S. aberta) */}
                    {order.status === 'open' && (userRole === 'admin' || user?.is_master) && (
                      <button
                        onClick={() => openEditModal(order)}
                        className="p-2 bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-400 rounded-lg transition-colors"
                        data-testid={`swap-machine-order-${order.id}`}
                        title="Trocar Máquina / Atualizar O.S. (re-rastreia horímetro)"
                      >
                        <RefreshCw size={18} />
                      </button>
                    )}
                    {/* Botão Fechar O.S. rápida - apenas em O.S. abertas e com permissão de edição */}
                    {order.status === 'open' && podeEditarOS(order) && (
                      <button
                        onClick={() => openQuickCloseModal(order)}
                        className="p-2 bg-green-500/15 hover:bg-green-500/25 text-green-400 rounded-lg transition-colors"
                        data-testid={`quick-close-order-${order.id}`}
                        title="Fechar O.S. rápida (GPS + horímetro final)"
                      >
                        <CheckCircle size={18} />
                      </button>
                    )}
                    {/* Botão Reabrir O.S. - apenas em O.S. fechadas e com permissão */}
                    {podeReabrirOS(order) && (
                      <button
                        onClick={() => openReopenModal(order)}
                        className="p-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 rounded-lg transition-colors"
                        data-testid={`reopen-order-${order.id}`}
                        title="Reabrir O.S. (motivo obrigatório)"
                      >
                        <RotateCcw size={18} />
                      </button>
                    )}
                    {/* Botão Cancelar O.S. - visível para perfis com permissão de edição em O.S. não-canceladas */}
                    {podeCancelarOS(order) && (
                      <button
                        onClick={() => openCancelModal(order)}
                        className="p-2 bg-orange-500/15 hover:bg-orange-500/25 text-orange-400 rounded-lg transition-colors"
                        data-testid={`cancel-order-${order.id}`}
                        title="Cancelar O.S. (motivo obrigatório)"
                      >
                        <XCircle size={18} />
                      </button>
                    )}
                    {/* Botão PDF - Gerar PDF da O.S. individual */}
                    <button
                      onClick={() => handleDownloadPDF(order.id, order.order_number, order.os_type)}
                      className="p-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg transition-colors"
                      data-testid={`pdf-order-${order.id}`}
                      title="Baixar PDF"
                    >
                      <FileText size={18} />
                    </button>
                    
                    {/* Botão WhatsApp - Compartilhar O.S. */}
                    <button
                      onClick={() => handleShareWhatsApp(order)}
                      className="p-2 bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded-lg transition-colors"
                      data-testid={`whatsapp-order-${order.id}`}
                      title="Compartilhar via WhatsApp"
                    >
                      <Share2 size={18} />
                    </button>
                    
                    {/* Botão Excluir - Apenas Admin/Gestor */}
                    {['admin', 'gestor'].includes(userRole) && (
                      <button
                        onClick={() => handleDelete(order.id)}
                        className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                        data-testid={`delete-order-${order.id}`}
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {order.observations && (
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <p className="text-gray-500 text-sm">
                    <span className="text-gray-400">Obs:</span> {order.observations}
                  </p>
                </div>
              )}

              {/* Fotos do Horímetro */}
              {(order.start_hour_meter_photo || order.end_hour_meter_photo) && (
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                    <Camera size={14} /> Fotos do Horímetro
                  </p>
                  <div className="flex gap-3">
                    {order.start_hour_meter_photo && (
                      <div className="flex-1">
                        <p className="text-gray-500 text-xs mb-1">Inicial</p>
                        <a href={order.start_hour_meter_photo} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={order.start_hour_meter_photo} 
                            alt="Horímetro inicial" 
                            className="w-full h-20 object-cover rounded-lg border border-gray-700 hover:border-yellow-500 transition-colors cursor-pointer"
                          />
                        </a>
                      </div>
                    )}
                    {order.end_hour_meter_photo && (
                      <div className="flex-1">
                        <p className="text-gray-500 text-xs mb-1">Final</p>
                        <a href={order.end_hour_meter_photo} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={order.end_hour_meter_photo} 
                            alt="Horímetro final" 
                            className="w-full h-20 object-cover rounded-lg border border-gray-700 hover:border-yellow-500 transition-colors cursor-pointer"
                          />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Histórico de Edições - Rastreabilidade */}
              {order.edit_history && order.edit_history.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <button 
                    onClick={() => setExpandedHistory(expandedHistory === order.id ? null : order.id)}
                    className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors w-full"
                  >
                    <History size={14} />
                    <span className="text-xs font-medium">
                      Histórico de Edições ({order.edit_history.length})
                    </span>
                    {expandedHistory === order.id ? (
                      <ChevronUp size={14} className="ml-auto" />
                    ) : (
                      <ChevronDown size={14} className="ml-auto" />
                    )}
                  </button>
                  
                  {expandedHistory === order.id && (
                    <div className="mt-3 space-y-3">
                      {order.edit_history.slice().reverse().map((edit, index) => (
                        <div 
                          key={index} 
                          className={`border rounded-lg p-3 text-xs ${edit.type === 'reopened' ? 'bg-amber-500/10 border-amber-500/30' : 'bg-gray-900/50 border-gray-800'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {edit.type === 'reopened' ? (
                                <RotateCcw size={12} className="text-amber-400" />
                              ) : (
                                <User size={12} className="text-yellow-500" />
                              )}
                              <span className="text-white font-medium">{edit.user_name}</span>
                              <span className="text-gray-500">({translateRole(edit.user_role)})</span>
                              {edit.type === 'reopened' && (
                                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded text-[10px] font-semibold">REABERTA</span>
                              )}
                            </div>
                            <span className="text-gray-500">{formatDateTime(edit.timestamp)}</span>
                          </div>
                          
                          {edit.type === 'reopened' ? (
                            <div className="space-y-1">
                              <div className="text-amber-200">
                                <span className="text-amber-400 font-semibold">Motivo:</span> {edit.motivo || '(não informado)'}
                              </div>
                              {edit.previous_end_hour_meter != null && (
                                <div className="text-gray-400 text-[11px]">
                                  Horímetro final anterior: <span className="text-gray-300 font-mono">{edit.previous_end_hour_meter}h</span>
                                </div>
                              )}
                            </div>
                          ) : edit.status_at_edit === 'closed' && (
                            <div className="mb-2 px-2 py-1 bg-orange-500/10 border border-orange-500/20 rounded text-orange-400 text-xs">
                              ⚠️ Editada enquanto fechada
                            </div>
                          )}
                          
                          {edit.type !== 'reopened' && Object.keys(edit.changes || {}).length > 0 ? (
                            <div className="space-y-1">
                              {Object.entries(edit.changes).map(([field, change]) => (
                                <div key={field} className="text-gray-400">
                                  <span className="text-gray-500">{translateFieldName(field)}:</span>{' '}
                                  {field.includes('photo') ? (
                                    <span className="text-blue-400">Foto atualizada</span>
                                  ) : (
                                    <>
                                      <span className="text-red-400 line-through">{formatChangeValue(change && typeof change === 'object' ? change.from : change)}</span>
                                      {' → '}
                                      <span className="text-green-400">{formatChangeValue(change && typeof change === 'object' ? change.to : undefined)}</span>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : edit.type !== 'reopened' && (
                            <span className="text-gray-500">Acesso registrado (sem alterações de campos)</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#111] border border-gray-800 rounded-xl p-12 text-center">
          <AlertCircle size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-white text-lg font-semibold mb-2">Nenhuma O.S. encontrada</h3>
          <p className="text-gray-500 mb-6">
            {search ? 'Tente ajustar sua busca' : 'Comece registrando horas trabalhadas'}
          </p>
          {!search && (
            <button
              onClick={openNewModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
            >
              <Plus size={20} />
              Criar O.S.
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-800">
              <h2 className="text-xl font-semibold text-white">
                {editingOrder ? 'Editar O.S.' : 'Nova Ordem de Serviço'}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4">
              {/* GPS Status Indicator */}
              {!editingOrder && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  form.start_location 
                    ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                    : gettingLocation 
                      ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                      : 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                }`}>
                  <MapPin size={16} />
                  {gettingLocation ? (
                    <span>Capturando localização GPS...</span>
                  ) : form.start_location ? (
                    <span>
                      📍 GPS capturado ({form.start_location.latitude.toFixed(6)}, {form.start_location.longitude.toFixed(6)})
                    </span>
                  ) : (
                    <span>GPS não disponível (permissão negada ou sem sinal)</span>
                  )}
                </div>
              )}
              
              {!editingOrder && (
                <>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Data *</label>
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({...form, date: e.target.value})}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                      required
                      data-testid="input-date"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Tipo de O.S. *</label>
                    <select
                      value={form.tipo_os}
                      onChange={(e) => setForm({...form, tipo_os: e.target.value})}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                      required
                      data-testid="input-tipo-os"
                    >
                      <option value="">Selecione o tipo de O.S.</option>
                      <option value="Operacional">Operacional</option>
                      {!isOperator && (
                        <>
                          <option value="Manutenção Preventiva">Manutenção Preventiva</option>
                          <option value="Manutenção Corretiva">Manutenção Corretiva</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Local de Operação *</label>
                    <select
                      value={form.location_id}
                      onChange={(e) => setForm({...form, location_id: e.target.value})}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                      required
                      data-testid="input-location"
                    >
                      <option value="">Selecione o local</option>
                      {activeLocations.map(location => (
                        <option key={location.id} value={location.id}>
                          {location.full_name}
                        </option>
                      ))}
                    </select>
                    {activeLocations.length === 0 && (
                      <p className="text-orange-400 text-sm mt-1">Nenhum local cadastrado. Cadastre um local primeiro.</p>
                    )}
                  </div>

                  {/* Etapa 1 Jul/2026 — Setor de Operação (obrigatório em O.S. Operacional
                      quando o Local possui setores ativos; Manutenção usa só o Local) */}
                  {setoresLocal.length > 0 && (!form.tipo_os || form.tipo_os === 'Operacional') && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Setor de Operação *</label>
                      <select
                        value={form.setor_operacao_id || ''}
                        onChange={(e) => setForm({...form, setor_operacao_id: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                        required={form.tipo_os === 'Operacional'}
                        data-testid="input-setor-operacao"
                      >
                        <option value="">Selecione o setor</option>
                        {setoresLocal.map((s) => (
                          <option key={s.id} value={s.id}>{s.nome}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Tipo de Operação *</label>
                    <select
                      value={form.operation_type}
                      onChange={(e) => setForm({...form, operation_type: e.target.value})}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                      required
                      data-testid="input-operation-type"
                    >
                      <option value="">Selecione o tipo</option>
                      {operationTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Máquina *</label>
                    <select
                      value={form.machine_id}
                      onChange={(e) => handleMachineSelect(e.target.value)}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                      required
                      data-testid="input-machine"
                    >
                      <option value="">Selecione a máquina</option>
                      {activeMachines.map(machine => (
                        <option key={machine.id} value={machine.id}>
                          {machine.brand} {machine.model} - {machine.serial_number} (Hor: {machine.current_hour_meter?.toFixed(1)}h)
                        </option>
                      ))}
                    </select>
                    {activeMachines.length === 0 && (
                      <p className="text-orange-400 text-sm mt-1">Nenhuma máquina ativa. Cadastre uma máquina primeiro.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Operador *</label>
                    <select
                      value={form.operator_id}
                      onChange={(e) => setForm({...form, operator_id: e.target.value})}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                      required
                      data-testid="input-operator"
                    >
                      <option value="">Selecione o operador</option>
                      {activeOperators.map(operator => (
                        <option key={operator.id} value={operator.id}>
                          {operator.name}
                        </option>
                      ))}
                    </select>
                    {activeOperators.length === 0 && (
                      <p className="text-orange-400 text-sm mt-1">Nenhum operador ativo. Cadastre um operador primeiro.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Horímetro Inicial *</label>
                    <input
                      type="number"
                      value={form.start_hour_meter}
                      onChange={(e) => setForm({...form, start_hour_meter: e.target.value})}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                      required
                      min="0"
                      step="0.1"
                      data-testid="input-start-hour"
                    />
                    <p className="text-gray-500 text-xs mt-1">Valor do horímetro no início do trabalho</p>
                  </div>

                  {/* Foto do Horímetro Inicial */}
                  <ImageUpload
                    value={form.start_hour_meter_photo}
                    onChange={(url) => setForm({...form, start_hour_meter_photo: url})}
                    label="Foto do Horímetro Inicial"
                    folder="horimetros/inicio"
                  />
                </>
              )}

              {editingOrder && (
                <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-sm mb-2">Informações da O.S.</p>
                  <p className="text-white">
                    <strong>Data:</strong> {formatDate(editingOrder.date)}
                  </p>
                  {editingOrder.location_info && (
                    <p className="text-white">
                      <strong>Local:</strong> {editingOrder.location_info.full_name}
                    </p>
                  )}
                  {/* Campo editável: Local de Operação — respeita o mesmo RBAC do PUT (editar_os) */}
                  <div className="mt-3">
                    <label className="block text-sm text-yellow-500 mb-2 font-semibold">
                      📍 Alterar Local de Operação
                    </label>
                    <select
                      value={form.location_id || ''}
                      onChange={(e) => setForm({...form, location_id: e.target.value})}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-yellow-500/50 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                      data-testid="input-change-location"
                    >
                      <option value="">Selecione o local</option>
                      {activeLocations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.full_name}
                        </option>
                      ))}
                    </select>
                    <p className="text-yellow-500/70 text-xs mt-1">Alteração fica registrada no histórico da O.S.</p>
                  </div>
                  {/* Etapa 1 Jul/2026 — Setor de Operação (edição, apenas O.S. Operacional) */}
                  {editingOrder.os_type === 'Operacional' && setoresLocal.length > 0 && (
                    <div className="mt-3">
                      <label className="block text-sm text-yellow-500 mb-2 font-semibold">
                        Setor de Operação
                      </label>
                      <select
                        value={form.setor_operacao_id || ''}
                        onChange={(e) => setForm({...form, setor_operacao_id: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-yellow-500/50 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                        data-testid="input-change-setor-operacao"
                      >
                        <option value="">Sem setor (O.S. anterior ao fluxo de setores)</option>
                        {setoresLocal.map((s) => (
                          <option key={s.id} value={s.id}>{s.nome}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {editingOrder.operation_type && (
                    <p className="text-white">
                      <strong>Tipo:</strong> {editingOrder.operation_type}
                    </p>
                  )}
                  <p className="text-white">
                    <strong>Máquina:</strong> {editingOrder.machine_info?.brand} {editingOrder.machine_info?.model}
                  </p>
                  
                  {/* Trocar máquina + re-rastreio do horímetro — APENAS ADMIN/MASTER e O.S. ABERTA */}
                  {(userRole === 'admin' || user?.is_master) && editingOrder.status !== 'closed' && (
                    <div className="mt-3 p-3 rounded-lg border border-yellow-500/40 bg-yellow-500/5">
                      <label className="block text-sm text-yellow-500 mb-2 font-semibold">
                        🔒 Trocar Máquina (Admin/Master)
                      </label>
                      <select
                        value={form.machine_id}
                        onChange={(e) => handleMachineSelect(e.target.value)}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-yellow-500/50 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                        data-testid="input-change-machine"
                      >
                        <option value="">Selecione a máquina</option>
                        {activeMachines.map(machine => (
                          <option key={machine.id} value={machine.id}>
                            {machine.brand} {machine.model} ({machine.serial_number})
                          </option>
                        ))}
                      </select>
                      {form.machine_id !== editingOrder.machine_id && (
                        <div className="mt-3">
                          <label className="block text-sm text-yellow-500 mb-1 font-semibold">
                            Horímetro inicial (nova máquina)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={form.start_hour_meter}
                            onChange={(e) => setForm({ ...form, start_hour_meter: e.target.value })}
                            className="w-full px-4 py-3 bg-[#0A0A0A] border border-yellow-500/50 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                            data-testid="input-swap-start-hour-meter"
                          />
                          <p className="text-yellow-500/70 text-xs mt-1">
                            Pré-preenchido com o horímetro atual da nova máquina. Ajuste se necessário antes de atualizar.
                          </p>
                        </div>
                      )}
                      <p className="text-yellow-500/70 text-xs mt-2">⚠️ Apenas Admin/Master podem alterar a máquina de uma O.S. aberta</p>
                    </div>
                  )}
                  
                  {/* Campo para trocar operador - APENAS ADMIN */}
                  {userRole === 'admin' ? (
                    <div className="mt-3">
                      <label className="block text-sm text-yellow-500 mb-2 font-semibold">
                        🔒 Trocar Operador (Admin)
                      </label>
                      <select
                        value={form.operator_id}
                        onChange={(e) => setForm({...form, operator_id: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-yellow-500/50 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                        data-testid="input-change-operator"
                      >
                        <option value="">Selecione o operador</option>
                        {activeOperators.map(operator => (
                          <option key={operator.id} value={operator.id}>
                            {operator.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-yellow-500/70 text-xs mt-1">⚠️ Apenas administradores podem alterar o operador</p>
                    </div>
                  ) : (
                    <p className="text-white">
                      <strong>Operador:</strong> {editingOrder.operator_info?.name}
                    </p>
                  )}
                  
                  <p className="text-white mt-2">
                    <strong>Horímetro Inicial:</strong> {editingOrder.start_hour_meter?.toFixed(1)}h
                  </p>
                  
                  {/* Exibir foto do horímetro inicial se existir */}
                  {editingOrder.start_hour_meter_photo && (
                    <div className="mt-3">
                      <p className="text-gray-400 text-xs mb-1">Foto Horímetro Inicial:</p>
                      <img 
                        src={editingOrder.start_hour_meter_photo} 
                        alt="Horímetro inicial" 
                        className="w-full h-32 object-cover rounded-lg border border-gray-700"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Bloco A — Campos da O.S. de Manutenção (Preventiva/Corretiva) */}
              {osTypeToDetalhado(editingOrder ? editingOrder.os_type : form.tipo_os) && (
                <MaintenanceFields
                  form={form}
                  setForm={setForm}
                  osDetalhado={osTypeToDetalhado(editingOrder ? editingOrder.os_type : form.tipo_os)}
                  canEditExecParada={canEditExecParada}
                  colaboradores={colaboradores}
                  almoxItens={almoxItens}
                  fornecedores={fornecedores}
                  checklistItems={checklistItems}
                  locations={activeLocations}
                  tiposServico={tiposServico}
                  defaultExecutorName={(editingOrder ? editingOrder.operator_info?.name : operators.find(o => o.id === form.operator_id)?.name) || ''}
                  requerSenha={editingOrder ? (editingOrder.requer_senha_fechamento !== false && editingOrder.status !== 'closed') : true}
                />
              )}

              {/* Peças e Materiais em O.S. Operacional (espelha a manutenção, sem foto) */}
              {!osTypeToDetalhado(editingOrder ? editingOrder.os_type : form.tipo_os)
                && (editingOrder ? editingOrder.os_type : form.tipo_os) === 'Operacional'
                && podeAplicarPecas && (
                <MaterialsSection
                  form={form}
                  setForm={setForm}
                  almoxItens={almoxItens}
                  showPhoto={false}
                />
              )}

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Horímetro Final {editingOrder?.status === 'open' && '(para fechar a O.S.)'}
                </label>
                <input
                  type="number"
                  value={form.end_hour_meter}
                  onChange={(e) => setForm({...form, end_hour_meter: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                  min={editingOrder ? editingOrder.start_hour_meter : form.start_hour_meter}
                  step="0.1"
                  placeholder="Deixe vazio para fechar depois"
                  data-testid="input-end-hour"
                />
                <p className="text-gray-500 text-xs mt-1">Valor do horímetro ao final do trabalho</p>
              </div>

              {/* Foto do Horímetro Final - Aparece quando há valor de horímetro final */}
              {(form.end_hour_meter || editingOrder?.end_hour_meter) && (
                <ImageUpload
                  value={form.end_hour_meter_photo}
                  onChange={(url) => setForm({...form, end_hour_meter_photo: url})}
                  label="Foto do Horímetro Final *"
                  folder="horimetros/fim"
                />
              )}

              <div>
                <label className="block text-sm text-gray-400 mb-2">Observações</label>
                <textarea
                  value={form.observations}
                  onChange={(e) => setForm({...form, observations: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors resize-none"
                  placeholder="Atividades realizadas, ocorrências..."
                  rows={3}
                  data-testid="input-observations"
                />
              </div>

              {/* Campo de itens de manutenção - visível para Técnicos */}
              <div>
                <label className="block text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <Wrench size={16} className="text-orange-500" />
                  Itens de Manutenção Aplicados
                </label>
                <textarea
                  value={form.maintenance_items}
                  onChange={(e) => setForm({...form, maintenance_items: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors resize-none"
                  placeholder="Ex: Filtro de óleo (1un), Óleo 15W40 (10L), Filtro de ar (1un)..."
                  rows={3}
                  data-testid="input-maintenance-items"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Liste os filtros, peças e lubrificantes aplicados nesta O.S.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                {osTypeToDetalhado(editingOrder ? editingOrder.os_type : form.tipo_os) ? (
                  editingOrder && editingOrder.status === 'closed' ? (
                    <button
                      type="button"
                      onClick={(e) => handleSubmit(e, {})}
                      disabled={formSubmitting}
                      className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="save-closed-work-order"
                      title="Salvar alterações nesta O.S. já fechada (com auditoria)"
                    >
                      {formSubmitting ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                  ) : (
                  <>
                    <button
                      type="button"
                      onClick={(e) => handleSubmit(e, { forceDraft: true })}
                      disabled={formSubmitting}
                      className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="save-draft-work-order"
                    >
                      {formSubmitting ? 'Salvando...' : 'Salvar Rascunho'}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleSubmit(e, {})}
                      disabled={formSubmitting || !maintReadyToClose(form, osTypeToDetalhado(editingOrder ? editingOrder.os_type : form.tipo_os))}
                      className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="close-work-order"
                      title={maintReadyToClose(form, osTypeToDetalhado(editingOrder ? editingOrder.os_type : form.tipo_os)) ? 'Fechar O.S.' : 'Preencha todos os campos e fotos obrigatórios'}
                    >
                      {formSubmitting ? 'Fechando...' : 'Fechar O.S.'}
                    </button>
                  </>
                  )
                ) : (
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-work-order"
                  >
                    {formSubmitting ? (editingOrder ? 'Atualizando...' : 'Criando...') : (editingOrder ? 'Atualizar O.S.' : 'Criar O.S.')}
                  </button>
                )}
              </div>

              {/* Bloco A — Checklist de pendências para fechar a O.S. de manutenção */}
              {(() => {
                const det = osTypeToDetalhado(editingOrder ? editingOrder.os_type : form.tipo_os);
                if (!det) return null;
                const missing = maintMissingItems(form, det);
                if (missing.length === 0) {
                  return (
                    <div className="mt-3 p-3 rounded-lg border border-green-600/40 bg-green-600/10 flex items-center gap-2" data-testid="maint-close-checklist-ok">
                      <CheckCircle2 size={16} className="text-green-400 shrink-0" />
                      <span className="text-sm text-green-300">Tudo pronto para fechar a O.S.</span>
                    </div>
                  );
                }
                return (
                  <div className="mt-3 p-3 rounded-lg border border-orange-500/40 bg-orange-500/10" data-testid="maint-close-checklist">
                    <p className="text-sm font-medium text-orange-300 mb-2">
                      Faltam {missing.length} item(ns) para fechar a O.S.:
                    </p>
                    <ul className="space-y-1">
                      {missing.map((m, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-gray-300" data-testid={`maint-missing-${i}`}>
                          <Circle size={10} className="text-orange-400 shrink-0" />
                          {m}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
            </form>
          </div>
        </div>
      )}

      {/* Modal: Cancelar O.S. */}
      {cancelModal.open && cancelModal.order && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <XCircle size={20} className="text-red-400" />
                <h3 className="text-lg font-semibold text-white">
                  Cancelar O.S. #{cancelModal.order.order_number || cancelModal.order.id?.slice(-6)}
                </h3>
              </div>
              <button
                onClick={() => setCancelModal({ open: false, order: null, motivo: '', submitting: false })}
                className="text-gray-400 hover:text-white"
                data-testid="close-cancel-modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-200">
                A O.S. será marcada como cancelada e movida para a aba "Canceladas". O histórico fica preservado e a ação é auditada. Esta operação não pode ser desfeita pela UI.
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  Motivo do cancelamento <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={cancelModal.motivo}
                  onChange={(e) => setCancelModal((s) => ({ ...s, motivo: e.target.value }))}
                  rows={4}
                  placeholder="Descreva o motivo do cancelamento (mínimo 10 caracteres)..."
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none transition-colors resize-none"
                  data-testid="input-cancel-motivo"
                />
                <p className="text-xs text-gray-500 mt-1">{(cancelModal.motivo || '').trim().length} / 10 caracteres mínimos</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setCancelModal({ open: false, order: null, motivo: '', submitting: false })}
                  className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  data-testid="cancel-cancel-button"
                >
                  Voltar
                </button>
                <button
                  onClick={handleCancelSubmit}
                  disabled={cancelModal.submitting || (cancelModal.motivo || '').trim().length < 10}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                  data-testid="confirm-cancel-button"
                >
                  {cancelModal.submitting ? 'Cancelando...' : 'Confirmar Cancelamento'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Reabrir O.S. */}
      {reopenModal.open && reopenModal.order && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <RotateCcw size={20} className="text-amber-400" />
                <h3 className="text-lg font-semibold text-white">
                  Reabrir O.S. #{reopenModal.order.order_number || reopenModal.order.id?.slice(-6)}
                </h3>
              </div>
              <button
                onClick={() => setReopenModal({ open: false, order: null, motivo: '', submitting: false })}
                className="text-gray-400 hover:text-white"
                data-testid="close-reopen-modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-200">
                Ao reabrir, o horímetro final, foto e localização de fechamento serão removidos e as horas da máquina revertidas. A ação ficará registrada no histórico da O.S.
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  Motivo da reabertura <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={reopenModal.motivo}
                  onChange={(e) => setReopenModal((s) => ({ ...s, motivo: e.target.value }))}
                  rows={4}
                  placeholder="Descreva o motivo da reabertura (mínimo 10 caracteres)..."
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition-colors resize-none"
                  data-testid="input-reopen-motivo"
                />
                <p className="text-xs text-gray-500 mt-1">{(reopenModal.motivo || '').trim().length} / 10 caracteres mínimos</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setReopenModal({ open: false, order: null, motivo: '', submitting: false })}
                  className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  data-testid="cancel-reopen-button"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReopenSubmit}
                  disabled={reopenModal.submitting || (reopenModal.motivo || '').trim().length < 10}
                  className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-colors"
                  data-testid="confirm-reopen-button"
                >
                  {reopenModal.submitting ? 'Reabrindo...' : 'Confirmar Reabertura'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ChecklistConfigModal
        open={showChecklistConfig}
        onClose={() => setShowChecklistConfig(false)}
        onSaved={() => {
          fetch(`${API_URL}/api/os-checklist-templates/effective`, { headers: authHeaders() })
            .then((r) => (r.ok ? r.json() : { items: [] }))
            .then((eff) => setChecklistItems((eff && eff.items) || []))
            .catch(() => {});
        }}
      />

      <DiariaPanelModal
        open={showDiariaPanel}
        onClose={() => setShowDiariaPanel(false)}
        canApprove={!!(user?.is_master || userRole === 'gestor')}
        onChanged={fetchData}
      />
    </div>
  );
}
